import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { uiStorage } from '../services/storageService';

// Theme types
const THEME_LIGHT = 'light';
const THEME_DARK = 'dark';

// Create theme context
const ThemeContext = createContext(null);

/**
 * ThemeProvider - Provides theme state and toggle functionality
 * 
 * Features:
 * - Persists theme preference in localStorage
 * - Auto-detects system preference on first visit
 * - Applies Tailwind dark mode class to document
 */
export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    // Check localStorage first
    const stored = uiStorage.get('theme');
    if (stored === THEME_LIGHT || stored === THEME_DARK) {
      return stored;
    }
    // Check system preference
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches 
        ? THEME_DARK 
        : THEME_LIGHT;
    }
    // Default to light
    return THEME_LIGHT;
  });

  // Apply theme class to document
  useEffect(() => {
    const root = document.documentElement;
    
    if (theme === THEME_DARK) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    
    // Persist to localStorage
    uiStorage.set('theme', theme);
  }, [theme]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      // Only auto-switch if user hasn't manually set a preference
      const stored = uiStorage.get('theme');
      if (!stored) {
        setThemeState(e.matches ? THEME_DARK : THEME_LIGHT);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Toggle between light and dark
  const toggleTheme = useCallback(() => {
    setThemeState(prev => prev === THEME_DARK ? THEME_LIGHT : THEME_DARK);
  }, []);

  // Set specific theme
  const setTheme = useCallback((newTheme) => {
    if (newTheme === THEME_LIGHT || newTheme === THEME_DARK) {
      setThemeState(newTheme);
    }
  }, []);

  const value = {
    theme,
    isDark: theme === THEME_DARK,
    darkMode: theme === THEME_DARK, // Alias for backward compatibility
    isLight: theme === THEME_LIGHT,
    toggleTheme,
    setTheme,
    THEME_LIGHT,
    THEME_DARK
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * useTheme hook - Access theme context
 */
export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

/**
 * Theme class utilities
 * Use these for conditional styling based on theme
 */
export const themeClasses = {
  // Backgrounds
  bgPrimary: 'bg-white dark:bg-gray-900',
  bgSecondary: 'bg-gray-50 dark:bg-gray-800',
  bgTertiary: 'bg-gray-100 dark:bg-gray-700',
  bgCard: 'bg-white dark:bg-gray-800',
  bgHover: 'hover:bg-gray-100 dark:hover:bg-gray-700',
  
  // Text
  textPrimary: 'text-gray-900 dark:text-white',
  textSecondary: 'text-gray-600 dark:text-gray-300',
  textMuted: 'text-gray-500 dark:text-gray-400',
  
  // Borders
  border: 'border-gray-200 dark:border-gray-700',
  borderLight: 'border-gray-100 dark:border-gray-800',
  
  // Inputs
  input: 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400',
  
  // Dividers
  divider: 'divide-gray-200 dark:divide-gray-700',
  
  // Shadows (more visible in dark mode)
  shadow: 'shadow-lg dark:shadow-2xl dark:shadow-black/20',
  
  // Combined page wrapper
  page: 'min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-white',
  
  // Header/Nav
  header: 'bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700',
  
  // Cards
  card: 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm dark:shadow-black/10',
  
  // Modal
  modalBackdrop: 'bg-black/50 dark:bg-black/70',
  modal: 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700',
};

/**
 * ThemeToggle component - Simple toggle button for theme switching
 */
export function ThemeToggle({ className = '' }) {
  const { isDark, toggleTheme } = useTheme();
  
  return (
    <button
      onClick={toggleTheme}
      className={`p-2 rounded-lg transition-colors ${themeClasses.bgHover} ${themeClasses.textSecondary} ${className}`}
      title={isDark ? 'Zum hellen Theme wechseln' : 'Zum dunklen Theme wechseln'}
      aria-label={isDark ? 'Zum hellen Theme wechseln' : 'Zum dunklen Theme wechseln'}
    >
      {isDark ? (
        // Sun icon for dark mode (clicking switches to light)
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ) : (
        // Moon icon for light mode (clicking switches to dark)
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
        </svg>
      )}
    </button>
  );
}

export default ThemeContext;
